import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, ArrowUpRight } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { NAV_LINKS } from "@/data/site";

const Monogram = () => (
  <div className="flex items-center gap-3" data-testid="header-logo">
    <div className="w-10 h-10 rounded-full border border-gold/60 flex items-center justify-center bg-black/40">
      <span className="font-display font-bold text-gold text-sm tracking-tight">WS</span>
    </div>
    <div className="leading-none">
      <p className="font-display text-lg text-cream tracking-tight">Waghe <span className="text-gold italic">&amp;</span> Sons</p>
      <p className="text-[9px] uppercase tracking-[0.32em] text-zinc-500 mt-1 whitespace-nowrap">Food Pvt. Ltd. · Est. 1965</p>
    </div>
  </div>
);

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,box-shadow] duration-500 border-b ${
        scrolled
          ? "bg-black/80 backdrop-blur-xl border-white/10 shadow-[0_10px_40px_rgba(0,0,0,0.45)]"
          : "bg-black/55 backdrop-blur-md border-white/5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[76px] flex items-center justify-between">
        <Link to="/" aria-label="Waghe and Sons home">
          <Monogram />
        </Link>

        <nav className="hidden xl:flex items-center gap-7" data-testid="desktop-nav">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `text-[13px] uppercase tracking-[0.14em] transition-colors duration-300 hover:text-gold ${
                  isActive ? "text-gold" : "text-zinc-300"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/export-inquiry"
            data-testid="header-cta-button"
            className="hidden sm:inline-flex items-center gap-2 bg-gold text-ink text-[13px] font-semibold uppercase tracking-[0.12em] px-5 py-2.5 rounded-sm transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-0.5 hover:shadow-[0_10px_30px_rgba(212,175,55,0.35)] hover:bg-gold-light"
          >
            Partner With Us <ArrowUpRight className="w-4 h-4" />
          </Link>

          <Sheet>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-button"
                aria-label="Open menu"
                className="xl:hidden w-11 h-11 border border-white/15 rounded-sm flex items-center justify-center text-cream hover:border-gold/60 hover:text-gold transition-colors duration-300"
              >
                <Menu className="w-5 h-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-ink border-white/10 w-[85vw] sm:w-[380px] p-0">
              <div className="p-7 border-b border-white/10">
                <Monogram />
              </div>
              <nav className="flex flex-col p-7 gap-1" data-testid="mobile-nav">
                {NAV_LINKS.map((l, i) => (
                  <SheetClose asChild key={l.to}>
                    <NavLink
                      to={l.to}
                      data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className={({ isActive }) =>
                        `py-3.5 border-b border-white/5 font-display text-2xl transition-colors duration-300 ${
                          isActive || (location.pathname === l.to) ? "text-gold" : "text-cream hover:text-gold"
                        }`
                      }
                    >
                      <span className="text-xs text-zinc-600 mr-3 font-body">0{i + 1}</span>
                      {l.label}
                    </NavLink>
                  </SheetClose>
                ))}
                <SheetClose asChild>
                  <Link
                    to="/export-inquiry"
                    data-testid="mobile-cta-button"
                    className="mt-6 inline-flex items-center justify-center gap-2 bg-gold text-ink text-sm font-semibold uppercase tracking-[0.12em] px-6 py-3.5 rounded-sm"
                  >
                    Partner With Us <ArrowUpRight className="w-4 h-4" />
                  </Link>
                </SheetClose>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
